/* eslint-disable indent */
import Cookies from "js-cookie";
import React, { FunctionComponent, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { IEpisodeWithProgress } from "../../models/anime-viewing-info";
import { SwipeDirection, useSwipeDetection } from "../../utils/anime-viewing/swiper-detection";
import VideoWithMediaSession from "./VideoWithMediaSession";
import "./VideoPlayer.css";
import { InitialPartyState, PartyContext } from "../../utils/party/party";
import DisconnectionModal from "./party/DisconnectionModal";
import { ViewingContext } from "../../utils/anime-viewing/viewing-context";
import { Size, SizeString, useVideoSize } from "../../utils/anime-viewing/video-size";

interface IProps {
	episode: IEpisodeWithProgress;
	isAutoPlay: boolean;
	autoNextEpisode: boolean;
	animeId: number;
	animeTitle: string;
	episodeTitle: string | null;
	autoSave: boolean;
	size: SizeString;
	setAutoSave: React.Dispatch<React.SetStateAction<boolean>>;
	setProgress: (episodeId: number, progress: number) => void;
	nextEpisode: () => void;
	previousEpisode: () => void;
	verifyEpisode: () => void;
	markEpisodeComplete: (episodeId: number) => void;
	setVideoSize(size: Size): void;
}

export let play: () => void;
export let pause: () => void;
export let seek: (progress: number) => void;
export let getState: () => InitialPartyState | void;

const VideoPlayer: FunctionComponent<IProps> = (props) => {
	const { episode, isAutoPlay, autoNextEpisode, animeId, animeTitle, episodeTitle, autoSave, size, setAutoSave, setProgress, nextEpisode, previousEpisode, verifyEpisode, markEpisodeComplete, setVideoSize } = props;
	
	const [wasStalled, setWasStalled] = useState<{currentTime: number} | false>(false);
	const video = useRef<HTMLVideoElement>(null);
	const canvas = useRef<HTMLCanvasElement>(null);

	const [isLoaded, setIsLoaded] = useState(false);
	const [isPlaying, setIsPlaying] = useState(false);
	const [waitingForChange, setWaitingForChange] = useState(false);
	const { info } = useContext(ViewingContext);

	useEffect(() => {
		if (!episode.id) { return; }
		setIsLoaded(false);
	}, [episode.id]);

	const source = useMemo(() => {
		if (!info?.mediaDomain || window.location.hostname === "localhost") {
			return `/animeViewing/getVideo2?episodeId=${episode.id}&accessToken=${info?.accessToken}&resolution=${info?.resolution}`;
		}
		return `https://${info?.mediaDomain}/animeViewing/getVideo2?episodeId=${episode.id}&accessToken=${info?.accessToken}&resolution=${info?.resolution}`;
	}, [episode.id, info?.accessToken, info?.mediaDomain, info?.resolution]);

	const { party, timeSynced, finishedJoining, play: partyPlay, pause: partyPause, 
		reportProgress: partyReportProgress, seek: partySeek } = useContext(PartyContext);

	useEffect(() => {
		console.log("syncing to party")
		
		if (!video.current || !isLoaded) { 
			console.log({
				videoExists: !!video.current,
				loaded: isLoaded,
			})
			return; }
		if (!party.isInParty) {
			console.log({
				isInParty: party.isInParty
			})
			return; }
		if (party.initialStateSet) {
			console.log({ initialStateSet: party.initialStateSet })
			return;
		}

		console.log({
			isPlaying: party.isPlaying,
			progress: party.progress,
		});

		if (party.progress !== undefined) {
				const skew = party.lastUpdate && party.isPlaying ? (new Date().getTime() - party.lastUpdate.getTime())/1000 : 0;
				console.log(`${skew} seconds off`);
				const seekedTime = Math.min(party.progress * video.current.duration + skew, video.current.duration);
				if (video.current.currentTime === seekedTime 
					|| (!video.current.paused && Math.abs(seekedTime - video.current.currentTime) < 3)) { 
					console.log("time is already synced");
				 } else {
					console.log("syncing time");
					console.log({
						currentTime: video.current.currentTime,
						seekedTime,
					})
					video.current.currentTime = seekedTime;
					timeSynced();
				 }
		}

		if (party.isPlaying !== undefined) {
			if (party.isPlaying) {
				if (!video.current.paused && isPlaying) {
					console.log("playing");
					finishedJoining();
					return;
				}
				console.log("play");
				video.current.play();
				
			}
			else {
				if (video.current.paused && !isPlaying) {
					console.log("paused");
					finishedJoining();
					return;
				}
				console.log("pause");
				video.current.pause();
				
			}
			return;
		}

		finishedJoining();
	}, [isLoaded, party.isInParty, party.progress, party.isPlaying, party.initialStateSet, isPlaying, party.lastUpdate, finishedJoining, timeSynced]);

	useEffect(() => {
		if (!video.current) { return; }

		play = () => {
			video.current?.play();
		}

		pause = () => {
			video.current?.pause();
		}

		seek = (progress: number) => {
			if (!video.current) { return; }
			const seekedTime = progress * video.current.duration;
			if (video.current.currentTime === seekedTime) { return; }
			video.current.currentTime = seekedTime;
		}

		getState = () => {
			if (!video.current) { return; }

			return {
				page: `/Anime/${animeId}/${episode.id}`,
				episodeId: episode.id,
				progress: video.current.currentTime / video.current.duration,
				isPlaying: !video.current.paused,
			}
		}

		return () => {
			play = () => {};
			pause = () => {};
			seek = () => {};
			getState = () => {};
		}
	}, [animeId, episode.id]);

	const onLoaded = useCallback(() => {
		if (!video.current) { return; }

		setIsLoaded(true);

		const volume = Number(Cookies.get("videoVolume") ?? 0.5);
		video.current.volume = volume;

		if (wasStalled) {
			setWasStalled(false);
			video.current.currentTime = wasStalled.currentTime;
			video.current.play();
			return;
		}

		if (episode.progress > 0 && (episode.progress <= 0.8 || (episode.progress < 1 && episode.lastProgressUpdate && new Date(episode.lastProgressUpdate) > new Date(new Date().getTime() - (7*24*60*60*1000))))) {
			video.current.currentTime = episode.progress * video.current.duration;
		}
		else {
			video.current.currentTime = 0;
		}

		if (isAutoPlay) {
			video.current.play();
		}

		setVideoSize({
			height: video.current.videoHeight,
			width: video.current.videoWidth,
		});
	}, [episode.lastProgressUpdate, episode.progress, isAutoPlay, setVideoSize, wasStalled]);

	const onPlaying = useCallback(() => {
		setAutoSave(true);
		partyPlay(episode.id);
		setIsPlaying(true);
	}, [episode.id, partyPlay, setAutoSave]);

	const saveProgress = useCallback((episodeId?: number) => {
		if (!video.current) { return; }
		setProgress(episodeId ?? episode.id, video.current.currentTime / video.current.duration);
		partyReportProgress(episodeId ?? episode.id, video.current.currentTime / video.current.duration);
	}, [episode.id, partyReportProgress, setProgress]);

	const onPause = useCallback(() => {
		saveProgress();
		setAutoSave(false);
		
		setIsPlaying(false);

		if (video.current?.currentTime !== video.current?.duration) {

			// Seeking can cause pause event to fire :(
			// readyState 4 supposedly means pause was manually done
			// readyState 1 means pause is from seek
			if (video.current?.readyState !== 1) {
				partyPause(episode.id);

			}
		}
	}, [episode.id, partyPause, saveProgress, setAutoSave]);

	useEffect(() => {
		if (!autoSave) { return; }

		const autoSaveTask = () => {
			return setTimeout(() => {
				if (video.current && !video.current.paused) {
					saveProgress(episode.id);
				}
				
				task = autoSaveTask();
			}, 10 * 1000);
		}
		let task = autoSaveTask();
		return () => clearTimeout(task);
	}, [episode.id, autoSave, saveProgress]);

	const onEnded = useCallback(() => {
		saveProgress();
		if (autoNextEpisode || isAutoPlay) {
			nextEpisode();
		}
	}, [autoNextEpisode, isAutoPlay, nextEpisode, saveProgress]);

	const onVolumeChange = useCallback((e: any) => {
		e.target.blur();
		Cookies.set("videoVolume", video.current?.volume + "", {
			expires: 365,
		});
	}, []);

	const keyBindings = useCallback((evnt: KeyboardEvent) => {
		if (["TEXTAREA", "INPUT"].includes((evnt.target as HTMLElement)?.tagName)) {
			return;
		}

		const vidPlayer = video.current;
		if (!vidPlayer) { return; }
		switch (evnt.key) {
			case "ArrowLeft":
				if ((evnt.target as HTMLElement).matches(".arrowTabElement")) { return; }
				vidPlayer.currentTime = (vidPlayer.currentTime > 1) ? vidPlayer.currentTime - 1 : 0;
				evnt.stopPropagation();
				evnt.preventDefault();
				return;
			case "ArrowRight":
				if ((evnt.target as HTMLElement).matches(".arrowTabElement")) { return; }
				vidPlayer.currentTime = (vidPlayer.currentTime < vidPlayer.duration) ? vidPlayer.currentTime + 1 : vidPlayer.duration;
				evnt.stopPropagation();
				evnt.preventDefault();
				return;
			case ",": // <
				previousEpisode(); return;
			case ".": // >
				nextEpisode(); return;
			case "1": // 1
			case "2": // 2
			case "3": // 3
			case "4": // 4
			case "5": // 5
			case "6": // 6
			case "7": // 7
			case "8": // 8
			case "9": // 9
			case "0": // 0
				vidPlayer.currentTime = ((evnt.which - 48) / 10) * vidPlayer.duration;
				return;
			case "/": // ?
				vidPlayer.requestFullscreen();
				return;
			case " ": // space
				(vidPlayer.paused) ? vidPlayer.play() : vidPlayer.pause();
				evnt.stopPropagation();
				evnt.preventDefault();
				return;
			case "c": // c
				vidPlayer.controls = !vidPlayer.controls;
				return;
			case "v": // v
				verifyEpisode();
				return;
			case "w": // w
				markEpisodeComplete(episode.id);
				return;
			default: return;
		}
	}, [episode.id, markEpisodeComplete, nextEpisode, previousEpisode, verifyEpisode]);

	useEffect(() => {
		document.addEventListener('keydown', keyBindings, true);
		return () => document.removeEventListener('keydown', keyBindings, true);
	}, [keyBindings]);

	useEffect(() => {
		if (!video.current) { return; }

		const onSeek = (e: any) => {
			if (!video.current) { return; }
			e.target.blur();
			partySeek(episode.id, video.current.currentTime / video.current.duration);
		}

		const currentVideo = video.current;
		currentVideo.addEventListener('seeked', onSeek);

		return () => {
			currentVideo.removeEventListener('seeked', onSeek);
		}
	}, [episode.id, partySeek]);

	useEffect(() => {
		const fullscreen = function (this: ScreenOrientation)  {
			const vidPlayer = video.current;
			if (!vidPlayer) { return; }
			if (this.type.indexOf("landscape") < 0 /*&& document.fullscreenElement*/) { 
				document.exitFullscreen();
				return; 
			}
			vidPlayer.requestFullscreen();
		}

		// eslint-disable-next-line no-restricted-globals
		screen.orientation?.addEventListener('change', fullscreen, true);
		// eslint-disable-next-line no-restricted-globals
		return () => screen.orientation?.removeEventListener('change', fullscreen, true);
	}, []);

	useEffect(() => {
		if (!video.current) { return; }
		video.current.currentTime = 0;
		video.current.src = source;
	}, [source]);

	const onSwipe = useCallback((direction: SwipeDirection) => {
		if (direction === SwipeDirection.Left) {
			nextEpisode();
			return;
		}
		if (direction === SwipeDirection.Right) {
			previousEpisode();
			return;
		}
	}, [nextEpisode, previousEpisode]);
	const { onTouchStart, onTouchMove, onTouchEnd } = useSwipeDetection(onSwipe);

	const onStalled = useCallback(() => {
		// return; // Think this might be doing more harm than good?
		// Idk.  Add back in to mitigate random pausing.
		if (!video.current) { return; }

		const err = video.current.error;
		console.log("Video error event fired!");
		console.log("Error code:", err?.code);
		console.log("Error message:", err?.message);

		// Network error or timeount || Corrupt media
		if (err?.code === 2 || err?.code === 3) {
			// if (navigator.userAgent.indexOf("AppleWebKit") >= 0) { return; }
			setWasStalled({ currentTime: video.current.currentTime });
			// video.current.src = source;
			video.current.load();
			setAutoSave(false);
		}
	}, [setAutoSave]);

	const onLoadStart = useCallback(() => {
		setIsLoaded(false);
	}, []);

	const paintBackdrop = useCallback(() => {
		if (!video.current || !canvas.current) { return; }
		// if (video.current.currentTime < 2) { return; }
		canvas.current.getContext("2d")?.drawImage(video.current, 0, 0, canvas.current.width, canvas.current.height);
		// console.log("paint");
	}, []);

	useEffect(() => {
		if (!isLoaded) { return; }
		paintBackdrop();

		let timeout: NodeJS.Timeout;

		const paint = () => {
			paintBackdrop();
			timeout = setTimeout(paint, 100);
		}

		timeout = setTimeout(paint, 100);

		return () => {
			clearTimeout(timeout);
		}
	}, [isLoaded, paintBackdrop]);

	return <div className="videoPlayerWrapper">
		<div className="videoBackground" style={{ display: isLoaded ? "unset" : "none" }}>
			<canvas className="videoBackdrop" ref={canvas} />
		</div>
		<VideoWithMediaSession id="videoPlayer" controls width={size.width} height={size.height} source={source} album={animeTitle} videoTitle={episodeTitle} artSrc={`/anime/animeImage/${animeId}`} video={video} 
			nextVideo={nextEpisode}
			prevVideo={previousEpisode}
			onLoaded={onLoaded}
			onPlaying={onPlaying}
			onPause={onPause}
			onEnded={onEnded}
			onVolumeChange={onVolumeChange}
			onTouchStart={onTouchStart}
			onTouchMove={onTouchMove}
			onTouchEnd={onTouchEnd}
			onStalled={onStalled}		
			onLoadStart={onLoadStart}
			onProgress={() => {}}
		/>
	</div>;
}

export default VideoPlayer;